import { 
  Building2, 
  FileCheck, 
  FileText, 
  CreditCard, 
  Shield, 
  ScrollText,
  Award,
  LucideIcon
} from "lucide-react";

export interface LegalDocumentConfig {
  key: string;
  doc_type: string;
  label: string;
  description: string;
  isEssential: boolean;
  validityDays: number | null;
  expiresShort?: boolean; // True pour Kbis < 3 mois
  keywords: string[];
}

export interface LegalCategoryConfig {
  key: string;
  label: string;
  icon: LucideIcon;
  documents: LegalDocumentConfig[];
}

// Configuration centralisée des documents légaux B2B - "Golden List Pro"
export const LEGAL_DOCS_CONFIG: Record<string, LegalCategoryConfig> = {
  company_identity: {
    key: "company_identity",
    label: "Identité Entreprise",
    icon: Building2,
    documents: [
      {
        key: "kbis",
        doc_type: "administrative",
        label: "Kbis / Extrait K",
        description: "Extrait du registre du commerce (moins de 3 mois)",
        isEssential: true,
        validityDays: 90,
        expiresShort: true,
        keywords: ["kbis", "extrait k", "registre commerce", "rcs", "greffe"],
      },
      {
        key: "statutes",
        doc_type: "legal",
        label: "Statuts",
        description: "Statuts de la société à jour",
        isEssential: true,
        validityDays: null,
        keywords: ["statuts", "société", "création", "modification", "assemblée"],
      },
      {
        key: "professional_card",
        doc_type: "administrative",
        label: "Carte professionnelle",
        description: "Carte professionnelle du secteur (si applicable)",
        isEssential: false,
        validityDays: 365,
        keywords: ["carte pro", "carte professionnelle", "agrément", "habilitation"],
      },
    ],
  },
  social: {
    key: "social",
    label: "Social",
    icon: FileCheck,
    documents: [
      {
        key: "urssaf_certificate",
        doc_type: "administrative",
        label: "Attestation URSSAF",
        description: "Attestation de vigilance sociale",
        isEssential: true,
        validityDays: 180,
        keywords: ["urssaf", "vigilance", "cotisations", "social", "attestation"],
      },
      {
        key: "employee_list",
        doc_type: "administrative",
        label: "Liste des salariés",
        description: "Liste nominative des salariés",
        isEssential: false,
        validityDays: null,
        keywords: ["salariés", "employés", "effectif", "personnel"],
      },
      {
        key: "dpae",
        doc_type: "administrative",
        label: "DPAE",
        description: "Déclaration préalable à l'embauche",
        isEssential: false,
        validityDays: null,
        keywords: ["dpae", "embauche", "déclaration", "préalable"],
      },
    ],
  },
  fiscal: {
    key: "fiscal",
    label: "Fiscal",
    icon: FileText,
    documents: [
      {
        key: "fiscal_regularity",
        doc_type: "administrative",
        label: "Régularité fiscale",
        description: "Attestation de régularité fiscale",
        isEssential: true,
        validityDays: 365,
        keywords: ["fiscal", "régularité", "impôts", "tva", "attestation"],
      },
      {
        key: "cfe_notice",
        doc_type: "tax",
        label: "Avis CFE",
        description: "Avis de cotisation foncière des entreprises",
        isEssential: false,
        validityDays: 365,
        keywords: ["cfe", "cotisation", "foncière", "entreprise", "avis"],
      },
      {
        key: "tax_returns",
        doc_type: "tax",
        label: "Liasse fiscale",
        description: "Dernière liasse fiscale (bilan, compte de résultat)",
        isEssential: false,
        validityDays: null,
        keywords: ["liasse", "fiscale", "bilan", "résultat", "comptable"],
      },
    ],
  },
  banking: {
    key: "banking",
    label: "Banque",
    icon: CreditCard,
    documents: [
      {
        key: "rib",
        doc_type: "financial",
        label: "RIB entreprise",
        description: "Relevé d'identité bancaire professionnel",
        isEssential: true,
        validityDays: null,
        keywords: ["rib", "bancaire", "iban", "bic", "compte pro"],
      },
    ],
  },
  insurance: {
    key: "insurance",
    label: "Assurances",
    icon: Shield,
    documents: [
      {
        key: "rc_pro_insurance",
        doc_type: "insurance",
        label: "Assurance RC Pro",
        description: "Responsabilité civile professionnelle",
        isEssential: true,
        validityDays: 365,
        keywords: ["rc pro", "responsabilité civile", "professionnelle", "assurance"],
      },
      {
        key: "decennial_insurance",
        doc_type: "insurance",
        label: "Assurance décennale",
        description: "Garantie décennale (BTP)",
        isEssential: false,
        validityDays: 365,
        keywords: ["décennale", "garantie", "btp", "construction", "10 ans"],
      },
      {
        key: "fleet_insurance",
        doc_type: "insurance",
        label: "Assurance flotte",
        description: "Assurance flotte automobile",
        isEssential: false,
        validityDays: 365,
        keywords: ["flotte", "automobile", "véhicules", "assurance auto"],
      },
    ],
  },
  qualifications: {
    key: "qualifications",
    label: "Qualifications",
    icon: Award,
    documents: [
      {
        key: "qualibat",
        doc_type: "certificate",
        label: "Qualibat",
        description: "Certification Qualibat (BTP)",
        isEssential: false,
        validityDays: 365,
        keywords: ["qualibat", "certification", "btp", "qualification"],
      },
      {
        key: "rge",
        doc_type: "certificate",
        label: "RGE",
        description: "Reconnu Garant de l'Environnement",
        isEssential: false,
        validityDays: 365,
        keywords: ["rge", "environnement", "garant", "énergie", "rénovation"],
      },
      {
        key: "other_certifications",
        doc_type: "certificate",
        label: "Autres certifications",
        description: "ISO, MASE, Qualiopi, etc.",
        isEssential: false,
        validityDays: 365,
        keywords: ["iso", "mase", "qualiopi", "certification", "qualité"],
      },
    ],
  },
};

// Liste des clés de catégories dans l'ordre d'affichage
export const LEGAL_CATEGORY_ORDER = [
  "company_identity",
  "social",
  "fiscal",
  "banking",
  "insurance",
  "qualifications",
];

// Helper pour récupérer tous les documents essentiels
export const getEssentialLegalDocuments = (): LegalDocumentConfig[] => {
  const essentialDocs: LegalDocumentConfig[] = [];
  LEGAL_CATEGORY_ORDER.forEach(categoryKey => {
    const category = LEGAL_DOCS_CONFIG[categoryKey];
    category.documents.forEach(doc => {
      if (doc.isEssential) {
        essentialDocs.push(doc);
      }
    });
  });
  return essentialDocs;
};

// Helper pour obtenir un document par sa clé
export const getLegalDocByKey = (key: string): LegalDocumentConfig | undefined => {
  for (const categoryKey of LEGAL_CATEGORY_ORDER) {
    const category = LEGAL_DOCS_CONFIG[categoryKey];
    const doc = category.documents.find(d => d.key === key);
    if (doc) return doc;
  }
  return undefined;
};

// Total des documents essentiels (pour le score principal)
export const TOTAL_ESSENTIAL_LEGAL_DOCS = getEssentialLegalDocuments().length;
